/**
 * Thin Ionicons renderer for the execute PWA — same icon set as native
 * (`@expo/vector-icons` / Ionicons). Icons ship as SVG data-URLs from the
 * `ionicons` package; outline glyphs rely on CSS classes
 * (`ionicon-stroke-width`, `ionicon-fill-none`) that we don't load, so we
 * expand those into real presentation attributes here.
 */
import type { CSSProperties } from 'react';

/** Matches ionicons.css `--ionicon-stroke-width` for outline icons. */
const IONICON_STROKE_WIDTH = 32;

interface Props {
  /** Data-URL from `ionicons/icons` (e.g. `arrowBackOutline`). */
  icon: string;
  size?: number;
  className?: string;
  style?: CSSProperties;
  title?: string;
  /**
   * Closed filled glyphs (home, folder, thumbs). Hybrid icons such as
   * `list` still stroke their bars — those keep stroke when the SVG
   * already carries a stroke-width.
   */
  filled?: boolean;
}

function prepareSvg(dataUrl: string, size: number, filled = false): string {
  const raw = decodeURIComponent(dataUrl.replace(/^data:image\/svg\+xml;utf8,/, ''));

  // Drop ionicon helper classes — we inline their CSS equivalents below.
  let svg = raw.replace(/\sclass=(['"])[^'"]*\1/g, '');

  if (filled) {
    // `list` (and a few others) mix filled dots with stroked bars.
    // Killing stroke leaves only the dots, which reads as a kebab.
    const hybrid = /stroke-width=/i.test(svg);
    svg = svg.replace(
      '<svg ',
      `<svg width="${size}" height="${size}" fill="currentColor" stroke="${hybrid ? 'currentColor' : 'none'}" `,
    );
    return svg;
  }

  // Always set a root stroke-width. Some outline glyphs put it on only a
  // few children (card, help); the rest would otherwise paint at 1px in
  // a 512 viewBox and vanish.
  svg = svg.replace(
    '<svg ',
    `<svg width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="${IONICON_STROKE_WIDTH}" `,
  );

  // Ensure shapes don't paint a default black fill (open paths especially).
  svg = svg.replace(
    /<(path|rect|circle|ellipse|line|polyline|polygon)\b/g,
    '<$1 fill="none"',
  );

  return svg;
}

export default function IonIcon({
  icon,
  size = 20,
  className = '',
  style,
  title,
  filled = false,
}: Props) {
  const svg = prepareSvg(icon, size, filled);

  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center text-current ${className}`}
      style={{ width: size, height: size, ...style }}
      dangerouslySetInnerHTML={{ __html: svg }}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    />
  );
}
